import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaHome, 
  FaUser, 
  FaAppleAlt, 
  FaPhoneAlt, 
  FaShoppingCart, 
  FaUserCircle, 
  FaBars, 
  FaTimes,
  FaBookMedical,
  FaMedkit,
  FaHospital,
  FaHeartbeat
} from 'react-icons/fa';
import { GiMedicinePills, GiHealthPotion } from 'react-icons/gi';
import { useAuth } from '../AuthContext';

// Firebase imports
import { database } from '../firebase';
import { ref as dbRef, get } from 'firebase/database';
import logo from '../asstes/logo.png';

const Navbar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartItems, setCartItems] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Refs for click outside detection
  const mobileMenuRef = useRef(null);
  const hamburgerButtonRef = useRef(null);

  // Get user info from AuthContext
  const isLoggedIn = !!user;
  const userName = user?.name || user?.fullName || 'User';

  // Load cart items from Firebase
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userMobile = localStorage.getItem('userMobile');
    
    if (userId && userMobile) {
      const loadCartCount = async () => {
        try {
          const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
          const snapshot = await get(cartRef);
          
          if (snapshot.exists()) {
            const cartData = snapshot.val();
            const totalItems = Object.values(cartData).reduce((total, item) => total + item.quantity, 0);
            setCartItems(totalItems);
          } else {
            setCartItems(0);
          }
        } catch (error) {
          console.error('Error loading cart count from Firebase:', error);
          // Fallback to localStorage
          const cartData = JSON.parse(localStorage.getItem(`cart_${userId}`) || '[]');
          const totalItems = cartData.reduce((total, item) => total + item.quantity, 0);
          setCartItems(totalItems);
        }
      };
      
      loadCartCount();
    }
  }, [location]);

  // Function to handle logout
  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    window.location.href = '/';
  };

  // Function to check if a nav item is active
  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    return location.pathname.startsWith(path) && path !== '/';
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Click outside to close mobile menu - FIXED VERSION
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both mobile menu and hamburger button
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current && 
        !mobileMenuRef.current.contains(event.target) &&
        hamburgerButtonRef.current && 
        !hamburgerButtonRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    // Add event listener with capture phase to prevent immediate execution
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile menu with proper event handling
  const toggleMobileMenu = (event) => {
    if (event) {
      event.stopPropagation(); // Prevent event from bubbling up
    }
    setIsMobileMenuOpen(prev => !prev);
  };

  // Close mobile menu when clicking on link
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Home', path: '/', icon: <FaHome /> },
    { name: 'About Us', path: '/about', icon: <FaUser /> },
    { name: 'Products', path: '/products', icon: <GiMedicinePills /> },
    { name: 'Hospital', path: '/hospital', icon: <FaHospital /> },
    { name: 'Diet Plans', path: '/diet-plans', icon: <FaAppleAlt /> },
    { name: 'E-Books', path: '/ebooks', icon: <FaBookMedical /> },
    { name: 'Contact', path: '/contact', icon: <FaPhoneAlt /> },
    { name: 'BlogPage', path: '/blogs', icon: <FaMedkit /> },
  ];

  return (
    <>
      <nav className={`bg-gradient-to-r from-teal-700 via-emerald-700 to-teal-800 text-white shadow-2xl sticky top-0 z-50 backdrop-blur-lg transition-all duration-300 ${
        isScrolled ? 'bg-opacity-95 shadow-lg' : 'bg-opacity-90'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            
            {/* Logo Section */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <Link to="/" className="flex items-center space-x-2 sm:space-x-4" onClick={closeMobileMenu}>
                <div className="bg-white p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-lg">
                  <img src={logo} alt="Anusaya Platform Logo" className="h-10 w-10 sm:h-12 sm:w-12 object-contain" />
                </div>
                <div className="hidden xs:block">
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight bg-gradient-to-r from-white to-teal-200 bg-clip-text text-transparent">
                    Anusaya Platform
                  </h1>
                  <p className="text-xs text-teal-200 font-medium hidden sm:block">Trusted Healthcare Solutions</p>
                  <p className="text-xs text-teal-200 font-medium sm:hidden">Healthcare Solutions</p>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center space-x-1 xl:space-x-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center space-x-1 xl:space-x-2 group px-3 xl:px-4 py-2 rounded-lg xl:rounded-xl transition-all duration-300 min-w-[80px] xl:min-w-[100px] justify-center ${
                    isActive(link.path) 
                      ? 'bg-white/20 text-white' 
                      : 'hover:bg-white/10 text-white/90 hover:text-white'
                  }`}
                >
                  <span className="text-lg opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-transform">
                    {link.icon}
                  </span>
                  <span className={`font-semibold text-sm transition-colors whitespace-nowrap ${
                    isActive(link.path) ? 'text-white' : 'group-hover:text-teal-200'
                  }`}>
                    {link.name}
                  </span>
                  <div className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-teal-400 to-emerald-400 transition-transform duration-300 ${
                    isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}></div>
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
              
              {/* Cart Icon with Badge */}
              <Link 
                to="/cart" 
                className="relative group p-2 sm:p-3 rounded-lg sm:rounded-xl hover:bg-white/10 transition-all duration-300"
                onClick={closeMobileMenu}
              >
                <FaShoppingCart className="text-lg sm:text-xl group-hover:scale-110 transition-transform" />
                {cartItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold h-5 w-5 sm:h-6 sm:w-6 rounded-full flex items-center justify-center animate-pulse shadow-lg">
                    {cartItems > 9 ? '9+' : cartItems}
                  </span>
                )}
                <div className="absolute top-full right-0 mt-2 w-48 bg-white text-gray-800 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50 hidden sm:block">
                  <div className="p-4">
                    <div className="text-center mb-2">
                      <span className="font-bold text-teal-600">{cartItems} items in cart</span>
                    </div>
                    <Link to="/cart" className="block w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-center py-2 rounded-lg hover:opacity-90 transition-opacity">
                      View Cart
                    </Link>
                  </div>
                </div>
              </Link>

              {/* Login/Profile - Hidden on extra small screens */}
              <div className="hidden xs:block relative group">
                {isLoggedIn ? (
                  // Logged in state - Show user name and dropdown
                  <>
                    <button className="flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
                      <FaUserCircle className="text-lg sm:text-xl group-hover:scale-110 transition-transform" />
                      <span className="font-bold text-sm sm:text-base whitespace-nowrap">{userName}</span>
                    </button>
                    
                    {/* Logged In User Dropdown */}
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white/95 backdrop-blur-lg text-gray-800 rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 border border-teal-100 z-50">
                      <div className="p-4">
                        <div className="text-center mb-4">
                          <div className="bg-gradient-to-r from-teal-100 to-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-2">
                            <FaUserCircle className="text-3xl text-teal-600" />
                          </div>
                          <h4 className="font-bold text-gray-800">{userName}</h4>
                          <p className="text-sm text-gray-600">Welcome back!</p>
                        </div>
                        <div className="space-y-2">
                          <Link to="/profile" className="flex items-center space-x-3 p-3 hover:bg-teal-50 rounded-xl transition-colors border border-gray-100 hover:border-teal-200">
                            <span className="text-lg">⚙️</span>
                            <span className="font-medium text-gray-700">My Profile</span>
                          </Link>
                          <Link to="/my-orders" className="flex items-center space-x-3 p-3 hover:bg-teal-50 rounded-xl transition-colors border border-gray-100 hover:border-teal-200">
                            <span className="text-lg">📦</span>
                            <span className="font-medium text-gray-700">My Orders</span>
                          </Link>
                          <button 
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-3 p-3 hover:bg-red-50 rounded-xl transition-colors border border-gray-100 hover:border-red-200"
                          >
                            <span className="text-lg">🚪</span>
                            <span className="font-medium text-red-600">Logout</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  // Logged out state - Show login button
                  <>
                    <Link to="/login">
                      <button className="flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
                        <FaUserCircle className="text-lg sm:text-xl group-hover:scale-110 transition-transform" />
                        <span className="font-bold text-sm sm:text-base whitespace-nowrap">Login</span>
                      </button>
                    </Link>
                    
                    {/* Profile Dropdown */}
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white/95 backdrop-blur-lg text-gray-800 rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 border border-teal-100 z-50">
                      <div className="p-4">
                        <div className="text-center mb-4">
                          <div className="bg-gradient-to-r from-teal-100 to-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-2">
                            <FaUserCircle className="text-3xl text-teal-600" />
                          </div>
                          <h4 className="font-bold text-gray-800">Welcome Guest</h4>
                          <p className="text-sm text-gray-600">Login to access all features</p>
                        </div>
                        <div className="space-y-2">
                          <Link to="/login" className="flex items-center space-x-3 p-3 hover:bg-teal-50 rounded-xl transition-colors border border-gray-100 hover:border-teal-200">
                            <span className="text-lg">👤</span>
                            <span className="font-medium text-gray-700">Login to Account</span>
                          </Link>
                          <Link to="/register" className="flex items-center space-x-3 p-3 hover:bg-teal-50 rounded-xl transition-colors border border-gray-100 hover:border-teal-200">
                            <span className="text-lg">📝</span>
                            <span className="font-medium text-gray-700">Create New Account</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Mobile Menu Button - FIXED */}
              <button 
                ref={hamburgerButtonRef}
                onClick={toggleMobileMenu}
                className="lg:hidden text-xl sm:text-2xl p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>

          {/* Animated Underline */}
          <div className="h-1 bg-gradient-to-r from-teal-400 via-emerald-400 to-teal-400 animate-gradient-x"></div>
        </div>

        {/* Mobile Menu - FIXED with ref */}
        <div 
          ref={mobileMenuRef}
          id="mobile-menu"
          className={`lg:hidden absolute top-full left-0 right-0 bg-gradient-to-b from-teal-800 to-emerald-900 backdrop-blur-lg transition-all duration-500 ease-in-out overflow-hidden z-40 ${
            isMobileMenuOpen 
              ? 'max-h-[90vh] opacity-100 shadow-2xl visible' 
              : 'max-h-0 opacity-0 invisible'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <div className="container mx-auto px-4 py-4 sm:py-6 max-h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-teal-600 scrollbar-track-teal-900">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl hover:bg-white/10 transition-all duration-300 group"
                  role="menuitem"
                >
                  <div className="bg-white/20 p-2 sm:p-3 rounded-lg sm:rounded-xl group-hover:bg-white/30 transition-colors">
                    <span className="text-lg sm:text-xl">{link.icon}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-base sm:text-lg group-hover:text-teal-200">
                      {link.name}
                    </span>
                  </div>
                </Link>
              ))}
              
              {/* Additional Mobile Actions */}
              <div className="col-span-full mt-3 sm:mt-4 space-y-3 sm:space-y-4">
                <Link 
                  to="/cart"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                  role="menuitem"
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <FaShoppingCart className="text-lg sm:text-xl" />
                    <span className="font-semibold text-base sm:text-lg">Shopping Cart</span>
                  </div>
                  {cartItems > 0 && (
                    <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-2 sm:px-3 py-1 rounded-full text-sm font-bold">
                      {cartItems > 9 ? '9+' : cartItems} items
                    </span>
                  )}
                </Link>
                
                {isLoggedIn ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-xl">
                      <div className="bg-gradient-to-r from-teal-500 to-emerald-500 w-10 h-10 rounded-full flex items-center justify-center">
                        <FaUserCircle className="text-xl text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-teal-200">Logged in as</p>
                        <p className="font-bold text-white">{userName}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <Link 
                        to="/profile"
                        onClick={closeMobileMenu}
                        className="bg-white/20 text-white text-center py-3 rounded-xl font-bold hover:bg-white/30 transition-colors text-sm"
                        role="menuitem"
                      >
                        Profile
                      </Link>
                      <button 
                        onClick={() => {
                          handleLogout();
                          closeMobileMenu();
                        }}
                        className="bg-red-500/20 text-red-100 text-center py-3 rounded-xl font-bold hover:bg-red-500/30 transition-colors text-sm border border-red-500/30"
                        role="menuitem"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <Link 
                      to="/login"
                      onClick={closeMobileMenu}
                      className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-center py-3 rounded-xl font-bold hover:opacity-90 transition-opacity text-sm sm:text-base"
                      role="menuitem"
                    >
                      Login
                    </Link>
                    <Link 
                      to="/register"
                      onClick={closeMobileMenu}
                      className="bg-white/20 text-white text-center py-3 rounded-xl font-bold hover:bg-white/30 transition-colors text-sm sm:text-base"
                      role="menuitem"
                    >
                      Register
                    </Link>
                  </div>
                )}
              </div>
            </div>
            
            {/* Emergency Contact in Mobile Menu */}
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-red-600/20 to-pink-600/20 rounded-xl sm:rounded-2xl border border-red-300/30">
              <div className="flex items-center space-x-3">
                <div className="bg-red-500/20 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                  <FaPhoneAlt className="text-red-300 text-lg sm:text-xl" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-red-100">24/7 Emergency</p>
                  <p className="font-bold text-white text-sm sm:text-base">+91 98765 43210</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={closeMobileMenu}
          role="presentation"
        />
      )}

      {/* Styles for animations */}
      <style jsx>{`
        @keyframes gradient-x {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        
        /* Custom breakpoint for extra small screens */
        @media (min-width: 475px) {
          .xs\\:block {
            display: block !important;
          }
        }
        
        /* Smooth transitions for mobile menu */
        .transition-max-height {
          transition: max-height 0.5s ease-in-out;
        }
        
        /* Prevent body scroll when mobile menu is open */
        body:has(.lg\\:hidden .absolute.top-full.left-0.right-0.max-h-\\[90vh\\]) {
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default Navbar;